import { CATALOG_PER_PAGE } from '~/settings.json';

const getDefaultState = () => {
  return {
    store: null,
    stats: null,
    products: null,
    productsMaxConstant: 50,
    loading: {},
    errors: [],
    payments: [],
    sales: [],
    catalog: null,
    catalogPages: 1,
    catalogCurrentPage: 1,
    hasAccess: false,
    productStep: 'select-catalog-product'
  };
};

export const mutations = {
  SET_STORE(state, data) {
    state.store = data;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    const keys = Object.keys(error);
    keys.forEach((key) => {
      const errors = error[key];
      const errorsData = errors.map((error) => ({ field: key, error: error }));
      state.errors = [...state.errors, ...errorsData];
    });
  },
  CLEAN_FIELD_ERRORS(state, field) {
    state.errors = state.errors.filter((error) => error.field !== field);
  },
  ADD_PRODUCTS(state, products) {
    state.products = products;
  },
  ADD_CATALOG(state, catalog) {
    if (!state.catalog) {
      state.catalog = [];
    }
    state.catalog = catalog;
  },
  SET_CATALOG_CURRENT_PAGE(state, page) {
    state.catalogCurrentPage = page;
  },
  SET_CATALOG_PAGES(state, pages) {
    state.catalogPages = pages;
  },
  SET_HAS_ACCESS(state, hasAccess) {
    state.hasAccess = hasAccess;
  },
  SET_PAYMENTS(state, payments) {
    state.payments = payments;
  },
  SET_SALES(state, sales) {
    state.sales = sales;
  },
  SET_STATS(state, stats) {
    state.stats = stats;
  },
  CLEAR_STATE(state) {
    Object.assign(state, getDefaultState());
  },
  CLEAN_ALL_ERRORS(state) {
    state.errors = [];
  },
  SET_PRODUCT_STEP(state, productStep) {
    state.productStep = productStep;
  }
};

export const getters = {};

export const actions = {
  async LOAD_HAS_ACCESS({ commit }) {
    commit('SET_LOADING', true);
    try {
      const { data } = await this.$axiosV2.get('/store/store/has-access');
      commit('SET_HAS_ACCESS', data.data);
    } catch (error) {
      console.error('myStore | LOAD_HAS_ACCESS:', error);
    }
    commit('SET_LOADING', false);
  },
  async LOAD_STORE({ commit }) {
    commit('SET_LOADING', true);
    try {
      const { data } = await this.$axiosV2.get('/store/store/');
      commit('SET_STORE', data.data);
    } catch (error) {
      console.error('myStore | LOAD_STORE:', error);
    }
    commit('SET_LOADING', false);
  },
  async LOAD_STATS({ commit }) {
    commit('SET_LOADING', true);
    try {
      const { data } = await this.$axiosV2.get('/store/store/stats');
      commit('SET_STATS', data.data);
    } catch (error) {
      console.error('myStore | LOAD_STATS:', error);
    }
    commit('SET_LOADING', false);
  },
  async LOAD_PAYMENTS({ commit }) {
    commit('SET_LOADING', true);
    try {
      const { data } = await this.$axiosV2.get('/store/store/payments');
      commit('SET_PAYMENTS', data.data);
    } catch (error) {
      console.error('myStore | LOAD_PAYMENTS:', error);
    }
    commit('SET_LOADING', false);
  },
  async LOAD_SALES({ commit }) {
    commit('SET_LOADING', true);
    try {
      const { data } = await this.$axiosV2.get('/store/store/sales');
      commit('SET_SALES', data.data);
    } catch (error) {
      console.error('myStore | LOAD_SALES:', error);
    }
    commit('SET_LOADING', false);
  },
  async LOAD_SALE({ commit, state }, { id }) {
    const alreadyLoaded = state.sales && state.sales.some((sale) => sale.id == id);
    if (alreadyLoaded) {
      return;
    }
    commit('SET_LOADING', true);
    try {
      const { data } = await this.$axiosV2.get(`/store/store/sales/${id}/`);
      commit('SET_SALES', [data.data]);
    } catch (error) {
      console.error('myStore | LOAD_SALE:', error);
    }
    commit('SET_LOADING', false);
  },
  async LOAD_PRODUCTS({ commit }, { page } = { page: 1 }) {
    commit('SET_LOADING', true);
    try {
      const params = { page };
      const { data } = await this.$axiosV2.get('/store/product/', { params });
      const results = data.data.results;
      commit('ADD_PRODUCTS', results);
    } catch (error) {
      console.error('myStore | LOAD_PRODUCTS:', error);
    }
    commit('SET_LOADING', false);
  },
  async LOAD_PRODUCT({ commit, state }, { id }) {
    const alreadyLoaded = state.products && state.products.some((product) => product.id == id);
    if (alreadyLoaded) {
      return;
    }
    commit('SET_LOADING', true);
    try {
      const { data } = await this.$axiosV2.get(`/store/product/${id}/`);
      commit('ADD_PRODUCTS', [data.data]);
    } catch (error) {
      console.error('myStore | LOAD_PRODUCT:', error);
    }
    commit('SET_LOADING', false);
  },
  async LOAD_CATALOG({ commit, state }) {
    commit('SET_LOADING', true);
    try {
      const { data } = await this.$axiosV2.get(
        `/store/printful/catalog?page=${state.catalogCurrentPage}&limit=${CATALOG_PER_PAGE}`
      );
      const { results, count } = data.data;
      commit('SET_CATALOG_PAGES', Math.ceil(count / CATALOG_PER_PAGE));
      commit('ADD_CATALOG', results);
    } catch (error) {
      console.error('myStore | LOAD_CATALOG:', error);
    }
    commit('SET_LOADING', false);
  },
  async CREATE_PRODUCT(
    { commit },
    { name, description, revenue_percentage, catalog_product_id, design }
  ) {
    commit('SET_LOADING', true);
    try {
      commit('CLEAN_ALL_ERRORS');
      const formData = new FormData();
      formData.append('name', name);
      formData.append('description', description);
      formData.append('revenue_percentage', revenue_percentage);
      formData.append('catalog_product_id', catalog_product_id);
      formData.append('design', design);
      const headers = { 'Content-Type': 'multipart/form-data' };
      const {
        data: { error, data }
      } = await this.$axiosV2.post('/store/product/', formData, { headers });
      if (error) {
        commit('SET_ERROR', data);
      }
    } catch (error) {
      const response = error.response;
      if (response && response.status === 400) {
        if (response.data.error) {
          commit('SET_ERROR', response.data.data);
        }
      } else {
        commit('SET_ERROR', { design: ['File too large, must be smaller than 10MB.'] });
      }
      console.error('myStore | CREATE_PRODUCT:', error);
    }
    commit('SET_LOADING', false);
  },
  async UPDATE_PRODUCT({ commit }, { id, name, description, revenue_percentage, design, status }) {
    commit('SET_LOADING', true);
    try {
      const formData = new FormData();
      if (name) formData.append('name', name);
      if (description) formData.append('description', description);
      if (revenue_percentage) formData.append('revenue_percentage', revenue_percentage);
      if (design) formData.append('design', design);
      if (status) formData.append('status', status);
      const headers = { 'Content-Type': 'multipart/form-data' };
      const {
        data: { error, data }
      } = await this.$axiosV2.put('/store/product/' + id + '/', formData, { headers });
      if (error) {
        commit('SET_ERROR', data);
      }
    } catch (error) {
      const response = error.response;
      if (response && response.status === 400) {
        if (response.data.error) {
          const errorParsed =
            typeof response.data.data === 'string'
              ? { non_field_errors: [response.data.data] }
              : response.data.data;
          commit('SET_ERROR', errorParsed);
        }
      } else {
        commit('SET_ERROR', { design: ['File too large, must be smaller than 10MB.'] });
      }
      console.error('myStore | UPDATE_PRODUCT:', error);
    }
    commit('SET_LOADING', false);
  },
  async CREATE_STORE({ commit }, { name, slug, contactEmail, description, logo }) {
    commit('SET_LOADING', true);
    try {
      commit('CLEAN_ALL_ERRORS');
      const formData = new FormData();
      formData.append('name', name);
      formData.append('slug', slug);
      formData.append('contact_email', contactEmail);
      formData.append('description', description);
      formData.append('logo', logo);
      const headers = { 'Content-Type': 'multipart/form-data' };
      const {
        data: { error, data }
      } = await this.$axiosV2.post('/store/store/', formData, { headers });
      if (error) {
        commit('SET_ERROR', data);
      }
    } catch (error) {
      const response = error.response;
      if (response && response.status === 400) {
        if (response.data.error) {
          commit('SET_ERROR', response.data.data);
        }
      }
      console.error('myStore | CREATE_STORE:', error);
    }
    commit('SET_LOADING', false);
  },
  async UPDATE_STORE({ commit }, { name, description, logo, status, paymentsEmail, contactEmail }) {
    commit('SET_LOADING', true);
    try {
      const formData = new FormData();
      if (name) formData.append('name', name);
      if (description) formData.append('description', description);
      if (logo) formData.append('logo', logo);
      if (status) formData.append('status', status);
      if (paymentsEmail) formData.append('owner_paypal_payment_email', paymentsEmail);
      if (contactEmail) formData.append('contact_email', contactEmail);
      const headers = { 'Content-Type': 'multipart/form-data' };
      const {
        data: { error, data }
      } = await this.$axiosV2.put('/store/store/', formData, { headers });
      if (error) {
        commit('SET_ERROR', data);
      }
    } catch (error) {
      const response = error.response;
      if (response && response.status === 400) {
        if (response.data.error) {
          commit('SET_ERROR', response.data.data);
        }
      }
      console.error('myStore | UPDATE_STORE:', error);
    }
    commit('SET_LOADING', false);
  },
  CLEAN_ERROR({ commit }, field) {
    commit('CLEAN_FIELD_ERRORS', field);
  },
  ADD_ERROR({ commit }, { field, error }) {
    const data = {
      [field]: [error]
    };
    commit('SET_ERROR', data);
  }
};

export const state = getDefaultState;
